import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FadeLoader } from 'react-spinners';
import {
  Card,
  Box,
  Grid,
  Divider,
  Container,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import Page from '../../components/Page';
import tracker from '../../api/tracker';
import { useApi } from '../../hooks/useApi';
import { fDateTime } from '../../utils/formatTime';
import { StatusCell } from '../../components/tables/AgGridWrapper';
import RescheduleDialog from './RescheduleDialog';

const override = { margin: '0 auto' };

export default function MeetingView() {
  const { hashid } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { doSave } = useApi();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);

  const fetchMeeting = async () => {
    setLoading(true);
    try {
      const response = await tracker.get(`meetings/${hashid}`);
      setMeeting(response.data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeeting();
  }, [hashid]);

  const onReschedule = async (dates) => {
    await doSave('meetings', dates, t('meeting_rescheduled_success'), null, meeting, {}, '', '/reschedule');
    setShowRescheduleDialog(false);
    fetchMeeting();
  };

  const m = meeting;

  return (
    <Page title={t('meetings')}>
      <Container maxWidth="xl">
        {loading || !m ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <FadeLoader cssOverride={override} color="#032C65" />
          </Box>
        ) : (
          <Card sx={{ p: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 3 }}
            >
              <Stack spacing={0.5}>
                <Typography variant="h5" fontWeight={700}>
                  {m.fullname || `${m.name || ''} ${m.lastname || ''}`.trim()}
                </Typography>
                <StatusCell value={m.status} t={t} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/dashboard/meeting')}
                >
                  {t('Go_back')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EventAvailableIcon />}
                  onClick={() => setShowRescheduleDialog(true)}
                  sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                >
                  {t('reschedule')}
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    fontWeight={700}
                    sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}
                  >
                    {t('contact_info')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow label={t('name')} value={m.fullname || `${m.name || ''} ${m.lastname || ''}`.trim()} />
                  <InfoRow label={t('email')} value={m.email} />
                  <InfoRow label={t('phone')} value={m.phone} />
                  <InfoRow label={t('date')} value={m.date} />
                  <InfoRow label={t('status')} value={t(m.status) || m.status} />
                  <InfoRow label={t('created_at')} value={m.created_at ? fDateTime(m.created_at) : null} />
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    fontWeight={700}
                    sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}
                  >
                    {t('motive')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                      color: 'text.primary',
                    }}
                  >
                    {m.motive || '—'}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Card>
        )}

        {m && (
          <RescheduleDialog
            open={showRescheduleDialog}
            meeting={m}
            onClose={() => setShowRescheduleDialog(false)}
            onConfirm={onReschedule}
          />
        )}
      </Container>
    </Page>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}
